import { createTimeline } from "animejs";

// Define types for the toast options
type ToastPosition =
  | "nanotoast-bottom-center"
  | "nanotoast-bottom-left"
  | "nanotoast-bottom-right"
  | "nanotoast-top-center"
  | "nanotoast-top-left"
  | "nanotoast-top-right";

export type ToastType = "info" | "warning" | "success" | "error";

type ButtonTarget = "self" | "_blank" | "_self" | "_parent" | "_top";

interface ToastButton {
  text: string;
  url?: string;
  target?: ButtonTarget;
  class?: string;
}

interface ToastOptions {
  parentContainer?: string;
  pos?: ToastPosition;
  text?: string;
  asideIcon?: string | null;
  type?: ToastType;
  button?: ToastButton | null;
  zIndex?: number | null;
  hideDelay?: number | null;
  showDelay?: number | null;
  duration?: number;
  customClass?: string;
  before?: (toast: HTMLElement) => void;
  enter?: (toast: HTMLElement) => void;
  hide?: (toast: HTMLElement) => void;
  click?: (instance: NanoToast) => void;
}

class NanoToast {
  private colorsType: ToastType[];
  private timeout: number | null;
  private options: ToastOptions;
  private nanoToast: HTMLElement | null;
  private delay: number;

  constructor(options?: ToastOptions) {
    const defaults: ToastOptions = {
      parentContainer: "body",
      pos: "nanotoast-bottom-center",
      text: "",
      asideIcon: null,
      type: "info",
      button: null,
      zIndex: null,
      hideDelay: null,
      showDelay: null,
      duration: 300,
      customClass: "",
      before: () => {},
      enter: () => {},
      hide: () => {},
    };
    this.colorsType = ["info", "warning", "success", "error"];
    this.timeout = null;
    this.options = { ...defaults, ...options };
    this.nanoToast = null;
    this.delay = 0;
  }

  public Hide(): void {
    if (this.nanoToast) {
      this.animateOut(this.options, this.nanoToast);
    }
  }

  public Show(customParam?: ToastOptions): void {
    // Clearing the current timeout animationOut
    if (this.timeout !== null) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }

    this.options = { ...this.options, ...customParam };

    // Check if there is already an instance of that object,
    // if too many it will return to prevent spam
    if ($(`.${this.options.pos}`).length > 1) {
      return;
    }
    if ($(`.${this.options.pos}`).length > 0) {
      const instance = $(document).find(`.${this.options.pos}`);
      if (instance[0]) {
        this.animateOut(this.options, instance[0] as HTMLElement);
      }
    }

    // Parent toast
    this.nanoToast = document.createElement("div");
    this.nanoToast.className = `nanotoast-container ${this.options.customClass || ""} ${
      this.options.pos || ""
    } ${this.GetColorType(this.options.type || "info")}`;

    if ($(".container-fluid").hasClass("pushed")) {
      this.nanoToast.classList.add("pushed-fixed-ui");
    }
    if ($(".container-fluid").hasClass("not-license")) {
      this.nanoToast.classList.add("with-menu");
    }

    if (this.options.type) {
      this.nanoToast.style.backgroundColor = this.GetColorType(
        this.options.type,
      );
    }

    if (this.options.zIndex) {
      this.nanoToast.style.zIndex = this.options.zIndex.toString();
    }

    // Inner group div
    const innerDiv = document.createElement("div");
    innerDiv.className = "inner-div";
    $(this.nanoToast).append(innerDiv);

    // Add close icon
    const close = document.createElement("i");
    close.className = "fas fa-times toast--close";
    $(close).on("click", () => this.Hide());
    $(this.nanoToast).append(close);

    // Add icon if not null
    if (this.options.asideIcon != null) {
      const i = document.createElement("i");
      if (
        typeof this.options.asideIcon === "string" &&
        this.options.asideIcon.startsWith("fa")
      ) {
        i.className = `${this.options.asideIcon}`;
        $(innerDiv).append(i);
      } else {
        throw new Error(
          `Is this a valid fontawesome prefix? '${this.options.asideIcon}'`,
        );
      }
    }

    // Adding text
    const p = document.createElement("p");
    if (this.options.text) {
      p.innerText = this.options.text;
    } else {
      console.error(`Cannot add text for '${this.options.text}'`);
    }
    $(innerDiv).append(p);

    // Adding button if not null
    if (this.options.button !== null && this.options.button !== undefined) {
      this.nanoToast.classList.add("has-button");
      const button = document.createElement("button");
      if (this.options.button.class !== undefined) {
        button.className = this.options.button.class;
      }
      const buttonLink = document.createElement("a");
      buttonLink.target = this.options.button.target
        ? this.options.button.target
        : "_self";

      // Looking for HREF element, if it's a div or a string
      if (this.options.button.url) {
        buttonLink.href = `${this.options.button.url}`;
        buttonLink.id = "nano-toast-link";
      } else {
        if (this.options.button) {
          this.options.button.url = "";
        }
      }

      // Looking for text element, if it's a div or a string
      if (this.options.button.text) {
        button.innerText = this.options.button.text;
      } else {
        throw new Error(
          `Cannot add button text for '${this.options.button.text}'`,
        );
      }

      $(buttonLink).append(button);
      $(innerDiv).append(buttonLink);

      // Add button click event
      $(button).on("click", () => {
        if (this.options.click && typeof this.options.click === "function") {
          this.options.click(this);
        }
      });
    }

    // Prepend toast to the container
    if (
      this.options.parentContainer &&
      $(this.options.parentContainer).length
    ) {
      $(this.options.parentContainer).prepend(this.nanoToast);
    } else {
      throw new Error(
        `Cannot find the parentContainer of '${this.options.parentContainer}'`,
      );
    }

    // Give a small delay to let last toast fadeOut
    // Prevent spamming glitch
    this.delay = 0;
    if (this.options.showDelay != null) {
      this.delay = this.options.showDelay;
    }

    this.timeout = window.setTimeout(() => {
      // Return callback before animation
      if (this.options.before && this.nanoToast) {
        this.options.before(this.nanoToast);
      }
      this.animateIn(this.options);
      if (this.timeout !== null) {
        clearTimeout(this.timeout);
        this.timeout = null;
      }
    }, this.delay);
  }

  private animateIn(options: ToastOptions): void {
    if (!this.nanoToast) return;
    const animation = createTimeline({
      autoplay: true,
      defaults: {
        duration: options.duration || 300,
        ease: "easeOutQuint",
      },
    });

    if (options.pos && options.pos.startsWith("nanotoast-bottom")) {
      animation.add(this.nanoToast, {
        opacity: "1",
      });
    }
    if (options.pos && options.pos.startsWith("nanotoast-top")) {
      animation.add(this.nanoToast, {
        opacity: "1",
      });
    }

    animation.onComplete = () => {
      if (options.enter && this.nanoToast) {
        options.enter(this.nanoToast);
      }
      if (options.hideDelay === null) {
        return;
      }
      this.timeout = window.setTimeout(() => {
        if (this.nanoToast) {
          this.animateOut(options, this.nanoToast);
        }
        if (this.timeout !== null) {
          clearTimeout(this.timeout);
          this.timeout = null;
        }
      }, options.hideDelay);
    };
  }

  private animateOut(options: ToastOptions, instance: HTMLElement): void {
    const animation = createTimeline({
      autoplay: true,
      defaults: {
        duration: options.duration || 300,
        ease: "easeOutQuint",
      },
    });

    if (options.pos && options.pos.startsWith("nanotoast-bottom")) {
      animation.add(this.nanoToast, {
        opacity: "0",
      });
    }
    if (options.pos && options.pos.startsWith("nanotoast-top")) {
      animation.add(this.nanoToast, {
        opacity: "0",
      });
    }

    animation.onComplete = () => {
      this.removeInstance(options, instance);
    };
  }

  private removeInstance(options: ToastOptions, instance: HTMLElement): void {
    $(instance).remove();
    // Return callback after removing object
    if (options.hide && this.nanoToast) {
      options.hide(this.nanoToast);
    }
  }

  private GetColorType(type: string): ToastType {
    for (let i = 0; i < this.colorsType.length; i += 1) {
      if (type === this.colorsType[i]) {
        return this.colorsType[i];
      }
    }
    throw new Error(`Can't find the color type for '${type}'`);
  }
}

export default NanoToast;
